import { Address } from "./Address";
import { Location } from "./Location";
import { Tenant } from "./Tenant";
import { LifvsUserEventDynamicData, LifvsUserMinimalModel } from "./User";
import { Distributor, ProductId, ProductMinimalModel } from "./Product";
import { PersonMinimalModel } from "./Person";
import { OrderUnit } from "./Order";
import { StoreDistributor } from "./StoreDistributor";
import { ImageInformation } from "./ImageInformation";

export type StoreState = "Closed" | "InSetup" | "Opened";

export type ControllerConnectionState = "Online" | "Unknown";

export enum DoorToOpen {
  Entrance = "Entrance",
  StorageDoor1 = "StorageDoor1",
  StorageDoor2 = "StorageDoor2"
}

export interface Store {
  id: string;
  externalId: string;
  name: string;
  address: Address;
  location: Location;
  state: StoreState;
  controllerConnectionState: ControllerConnectionState;
  isFranchise: boolean;
  cluster: Tenant;
  distributors: StoreDistributor[];
  config: StoreConfiguration;
  features?: SCFeatures;
  openingSchedule?: Schedule;
  pickupBoxes: Array<StorePickupBox>;
  logo?: ImageInformation;
  banner?: ImageInformation;
}

interface SCFeatures {
  readonly lifvsbonus?: boolean;
  readonly usesDigitalPriceTags?: boolean;
  clickNCollect?: boolean;
  otpForVerifiedUsers?: boolean;
  noStoreDoor?: boolean;
}

export interface ScheduleItem {
  type: "Manned" | "Unmanned" | null;
  from: string | null;
  to: string | null;
}

export type Day =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

export type Schedule = Record<Day, ScheduleItem[]>;
export type RawSchedule = Record<string, ScheduleItem[]>;

interface SCDoorFeatures {
  closingHours?: {
    from: string | null;
    to: string | null;
  };
  terminalId?: number;
  onlyAllowVerifiedUsers?: boolean;
  verifiedUserAgeLimit?: number;
  openDoorRequiredDistance?: number;
}

export interface AccessRestrictionSchedule {
  from: string | null;
  to: string | null;
}

export interface StoreConfiguration {
  features?: SCFeatures;
  door?: {
    onlyAllowVerifiedUsers?: boolean;
    verifiedUserAgeLimit?: number;
    verifiedUserTimeRestrictions?: AccessRestrictionSchedule;
  };
  priceTags?: any;
}

export interface OnBoardingStore {
  id?: string;
  name: string;
  address: Address;
}

export interface TenantStore {
  id: string;
  externalId: string;
  name: string;
  address: Address;
  location: Location;
  state: StoreState;
  isFranchise: boolean;
}

export interface StoreMinimalModel {
  id: string;
  name: string;
}

export interface StoreWriteModel {
  id?: string;
  name: string;
  address: Address;
  location: Location;
  state: StoreState;
  isFranchise: boolean;
  externalId: string;
  clusterId: string;
  cluster?: Tenant;
  distributors?: StoreDistributor[];
  config: StoreConfiguration;
  pickupBoxes: Array<StorePickupBox>;
  pickupBoxesChanged: boolean;
  logo?: ImageInformation;
  banner?: ImageInformation;
  openingSchedule?: RawSchedule;
}

export interface StoreEvent {
  userId: string;
  sessionId: string;
  event: string;
  timestamp: Date;
  successful: boolean;
  user: LifvsUserMinimalModel;
  dynamicData: LifvsUserEventDynamicData;
}

export type StorePickupBoxId = string;

export interface StorePickupBox {
  id: StorePickupBoxId;
  slot: number;
  terminalId: number;
}

export type StoreOrderId = string;
export type StoreOrderItemId = string;

export enum StoreOrderState {
  Proposed = "Proposed",
  Confirmed = "Confirmed",
  Ordered = "Ordered",
  Delivered = "Delivered"
}

export enum StoreOrderItemType {
  Generated = "Generated",
  ManuallyAdded = "ManuallyAdded"
}

export interface AwaitingStoreOrderProposal {
  readonly store: StoreMinimalModel;
  readonly count: number;
}

export interface StoreOrderItemStatistics {
  readonly inStockCurrently: number;
  readonly inStock: number;
  readonly totalQuantitySold: number;
  readonly actualNbrOfDaysSold: number;
  readonly salesSinceDate: Date;
  readonly firstSalesDate: Date;
  readonly lastSalesDate: Date;
  readonly productAddedDate: Date;

  readonly carry: number;
  readonly periodOfDays: number;
  readonly averageQuantitySold: number;
}

export interface StoreOrderItem {
  readonly id: StoreOrderItemId;
  productId?: string;
  readonly product: ProductMinimalModel;
  readonly distributor?: Distributor;
  readonly type: StoreOrderItemType;
  readonly delivered?: boolean;
  quantity: number;
  onShelfBeforeDelivery: number;
  discrepancyQuantity?: number;
  discrepancyReasonId?: number;
  currentlyOnShelf?: number;
  unit: OrderUnit;
  readonly distributionPackageSize?: number;
  readonly expectedDeliveryDurability?: number;
  readonly expectedDeliveryMinimumTemperature?: number;
  readonly expectedDeliveryMaximumTemperature?: number;
  readonly statistics?: StoreOrderItemStatistics;
  checked?: boolean;
}

export interface StoreOrder {
  readonly id: StoreOrderId;
  readonly isMerged: boolean;
  readonly stores: ReadonlyArray<StoreMinimalModel>;
  readonly distributors: ReadonlyArray<Distributor>;
  readonly state: StoreOrderState;
  readonly orderNotes?: string;
  readonly comment?: string;
  readonly confirmDeadline: Date;
  readonly confirmed?: Date;
  readonly confirmedBy: ReadonlyArray<PersonMinimalModel>;
  readonly orderDeadline: Date;
  readonly ordered?: Date;
  readonly orderedBy?: PersonMinimalModel;
  readonly estimatedDelivery: Date;
  readonly delivered?: Date;
  readonly deliveredBy: ReadonlyArray<PersonMinimalModel>;
  readonly lastUpdatedBy?: PersonMinimalModel;
  readonly updated: Date;
  readonly created: Date;
  readonly items: ReadonlyArray<StoreOrderItem>;
}

export interface MergeStoreOrders {
  readonly storeOrderIds: ReadonlyArray<StoreOrderId>;
  readonly comment?: string;
}

export interface RemoveOrder {
  readonly orderId: StoreOrderId;
  readonly isMerged: boolean;
}

export interface UpdateStoreOrder {
  readonly storeOrderId: StoreOrderId;
  state: StoreOrderState;
  comment?: string;
  updateItems: ReadonlyArray<UpdateStoreOrderItem>;
  deleteItems: ReadonlyArray<DeleteStoreOrderItem>;
  addItems: ReadonlyArray<AddStoreOrderItem>;
}

export interface AddStoreOrderItem {
  productId: ProductId;
  quantity: number;
  quantityUnit: OrderUnit;
}

export interface UpdateStoreOrderItem {
  readonly id: StoreOrderItemId;
  readonly quantity: number;
}

export interface DeleteStoreOrderItem {
  readonly id: StoreOrderItemId;
}

export const emptyStore = (): StoreWriteModel => ({
  externalId: "",
  name: "",
  address: {
    street: "",
    postalCode: "",
    city: ""
  },
  location: {
    longitude: 0,
    latitude: 0
  },
  state: "InSetup",
  isFranchise: false,
  clusterId: "",
  config: {},
  pickupBoxes: [],
  pickupBoxesChanged: false
});

export const toWriteModel = (
  store: Store,
  pickupBoxesChanged: boolean
): StoreWriteModel => {
  return {
    id: store.id,
    name: store.name,
    clusterId: store.cluster.id,
    address: store.address,
    location: store.location,
    state: store.state,
    isFranchise: store.isFranchise,
    externalId: store.externalId,
    config: store.config,
    openingSchedule: store.openingSchedule,
    pickupBoxes: store.pickupBoxes,
    pickupBoxesChanged: pickupBoxesChanged,
    logo: store.logo,
    banner: store.banner
  };
};
